import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

export default function AuthPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextPath = (() => {
    const raw = params.get("next") ?? "";
    return raw.startsWith("/") && !raw.startsWith("//") ? raw : "/scenarios";
  })();
  const { user, loading } = useAuth();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) nav(nextPath, { replace: true });
  }, [loading, user, nav, nextPath]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}${nextPath}`,
            data: { display_name: displayName || undefined },
          },
        });
        if (error) throw error;
        toast.success("Check your email to confirm your account.");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = async () => {
    setBusy(true);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}${nextPath}`,
    });
    if (result.error) {
      toast.error("Google sign-in failed");
      setBusy(false);
      return;
    }
    if (result.redirected) return;
    nav(nextPath, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Helmet>
        <title>Sign in — Family Loan Planner</title>
        <meta name="description" content="Sign in to save and revisit your Family Loan Planner scenarios." />
      </Helmet>
      <Card className="w-full max-w-md p-6 space-y-5">
        <div>
          <h1 className="text-xl font-semibold">{mode === "signin" ? "Sign in" : "Create your account"}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Save scenarios, return to them later, and regenerate commitment reports.
          </p>
        </div>

        <Button type="button" variant="outline" className="w-full" onClick={googleSignIn} disabled={busy}>
          Continue with Google
        </Button>

        <div className="relative text-center">
          <span className="bg-background px-2 text-xs text-muted-foreground relative z-10">or</span>
          <div className="absolute inset-0 top-1/2 border-t border-border" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <div>
              <Label htmlFor="dn">Display name</Label>
              <Input id="dn" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Jane Doe" />
            </div>
          )}
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="pw">Password</Label>
            <Input id="pw" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
          </Button>
        </form>

        <p className="text-sm text-center text-muted-foreground">
          {mode === "signin" ? (
            <>No account? <button className="underline" onClick={() => setMode("signup")}>Sign up</button></>
          ) : (
            <>Already have an account? <button className="underline" onClick={() => setMode("signin")}>Sign in</button></>
          )}
        </p>
        <p className="text-xs text-center"><Link to="/" className="text-muted-foreground hover:text-foreground">← Back to planner</Link></p>
      </Card>
    </div>
  );
}